/**
 * ADR-0180 slice 3b — merchant account-deletion client
 * (GET/POST/DELETE /v1/account/deletion).
 */

import type { HttpClient } from './http-client.js';
import { ApiError } from './http-client.js';

function explainApiError(err: ApiError): string {
  // A failed step-up code comes back as 403 with reason 'reauth_*' (the
  // API deliberately does NOT use 401, which would sign the owner out —
  // their session is valid, only the code was wrong). Owner-only refusal
  // is the other 403. Distinguish on the server-supplied reason.
  if (err.status === 403) {
    if (err.server_reason === 'reauth_required' || err.server_reason === 'reauth_invalid') {
      return 'That code is wrong or expired. Request a new one.';
    }
    return 'Only the account owner can do this.';
  }
  if (err.status === 401) return 'That code is wrong or expired. Request a new one.';
  if (err.status === 409) return 'No pending deletion to cancel.';
  if (err.status === 429) return 'Too many attempts — try again shortly.';
  // 502 with this reason = the API reached SES but the code email was
  // rejected/undeliverable (e.g. an address SES will not send to).
  if (err.server_reason === 'email_delivery_failed') {
    return 'We could not email the code to your address. Please try again, or contact support if it keeps failing.';
  }
  return 'Something went wrong. Please try again.';
}

export interface DeletionStatusWire {
  readonly pending: boolean;
  readonly requested_at: string | null;
  readonly grace_until: string | null;
}

export type DeletionResult<T> =
  | { readonly kind: 'ok'; readonly value: T }
  | { readonly kind: 'error'; readonly message: string };

async function call<T>(fn: () => Promise<T>): Promise<DeletionResult<T>> {
  try {
    return { kind: 'ok', value: await fn() };
  } catch (err) {
    if (err instanceof ApiError) {
      return { kind: 'error', message: explainApiError(err) };
    }
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

export function getDeletionStatus(
  http: HttpClient,
): Promise<DeletionResult<DeletionStatusWire>> {
  return call(async () => {
    const res = await http.request<DeletionStatusWire>({
      method: 'GET',
      path: '/v1/account/deletion',
    });
    if (res === undefined) throw new Error('Empty response body.');
    return res;
  });
}

export function requestDeletionChallenge(
  http: HttpClient,
): Promise<DeletionResult<{ sent: boolean }>> {
  return call(async () => {
    const res = await http.request<{ sent: boolean }>({
      method: 'POST',
      path: '/v1/account/deletion/challenge',
    });
    // 202 may carry no body in some clients; treat as success.
    return res ?? { sent: true };
  });
}

export function requestDeletion(
  http: HttpClient,
  reauthCode: string,
): Promise<DeletionResult<{ grace_until: string }>> {
  return call(async () => {
    const res = await http.request<{ grace_until: string }>({
      method: 'POST',
      path: '/v1/account/deletion',
      body: { reauth_code: reauthCode },
    });
    if (res === undefined) throw new Error('Empty response body.');
    return res;
  });
}

export function cancelDeletion(
  http: HttpClient,
): Promise<DeletionResult<{ sites_restored: number }>> {
  return call(async () => {
    const res = await http.request<{ sites_restored: number }>({
      method: 'DELETE',
      path: '/v1/account/deletion',
    });
    if (res === undefined) throw new Error('Empty response body.');
    return res;
  });
}
